import type { MediaRow } from '@/routes/media/types';
import type { PublishStatus } from '@/components/editor/PublishingSidebar';
import type { TranslatedValue } from '@/lib/forms/translatable';
import type { TaxonomyRef } from '@/routes/news/types';

/**
 * docs/09-BUILD-PLAN.md Phase 6 step 8 — the staff directory.
 *
 * Hand-written for the reason `lib/api/resource.ts` sets out: Scramble types a
 * translatable field as `{[key: string]: unknown}`.
 */

/** docs/03-DATA-MODEL.md §4's `rank` enum, in the order the filter shows it. */
export const STAFF_RANKS = [
  'professor',
  'associate_professor',
  'assistant_professor',
  'invited_lecturer',
  'invited_doctor',
  'researcher',
  'administrative',
] as const;

export type StaffRank = (typeof STAFF_RANKS)[number];

export interface StaffRow {
  id: number;
  first_name: TranslatedValue;
  last_name: TranslatedValue;
  slug: TranslatedValue;
  position: TranslatedValue;
  rank: StaffRank;
  faculty_id: number | null;
  faculty: TaxonomyRef | null;
  department_id: number | null;
  department: TaxonomyRef | null;
  email: string | null;
  phone: string | null;
  photo_media_id: number | null;
  photo: MediaRow | null;
  /*
   * `cover_url`, `photo_url` and `logo_url` were removed here.
   *
   * Task #71 moved images onto the media library and the API resources
   * went from a resolved URL string to the media object above. These
   * types kept declaring the old fields, so three list cells went on
   * reading `undefined` and rendering their empty state — the news list
   * showed a placeholder against 676 articles that had a cover, and
   * nothing failed. Use `thumbUrl(row.cover)`.
   */
  bio: TranslatedValue;
  education: TranslatedValue;
  publications: TranslatedValue;
  /*
   * Lists, not rich text — §4's table leaves the "rich text" note off these two
   * rows, the model's `htmlFields()` names only the other three, and the public
   * profile renders both as `<ul>`s.
   */
  research_interests: Record<string, string[] | null>;
  courses: Record<string, string[] | null>;
  orcid: string | null;
  scholar_url: string | null;
  researchgate_url: string | null;
  linkedin_url: string | null;
  is_academic: boolean;
  sort_order: number;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  created_by: { id: number; name: string } | null;
  updated_by: { id: number; name: string } | null;
}

export interface StaffInput {
  first_name: TranslatedValue;
  last_name: TranslatedValue;
  slug: TranslatedValue;
  position: TranslatedValue;
  rank: StaffRank;
  faculty_id: number | null;
  department_id: number | null;
  email: string | null;
  phone: string | null;
  photo_media_id: number | null;
  bio: TranslatedValue;
  education: TranslatedValue;
  publications: TranslatedValue;
  /*
   * Lists, not rich text — §4's table leaves the "rich text" note off these two
   * rows, the model's `htmlFields()` names only the other three, and the public
   * profile renders both as `<ul>`s.
   */
  research_interests: Record<string, string[] | null>;
  courses: Record<string, string[] | null>;
  orcid: string | null;
  scholar_url: string | null;
  researchgate_url: string | null;
  linkedin_url: string | null;
  is_academic: boolean;
  sort_order: number;
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
}

/* ── the importer, docs/05-ADMIN-DASHBOARD.md §5.5 ───────────────────── */

/** What `POST /admin/staff/import/analyse` answers with. */
export interface ImportAnalysis {
  headers: string[];
  /** Column index → field name, for the columns it recognised. */
  suggested_mapping: Record<string, string>;
  /** Every field a column may be mapped to. */
  fields: string[];
  sample: string[][];
  total: number;
}

export interface ImportRow {
  line: number;
  action: 'create' | 'update' | 'error';
  name: string;
  errors: string[];
}

export interface ImportResult {
  summary: { created: number; updated: number; failed: number };
  rows: ImportRow[];
  dry_run: boolean;
}
